import { Stack, Checkbox, Label, Separator } from "@fluentui/react";
import React, { useState, useRef, useEffect } from "react";
import type { KeyboardEvent, ClipboardEvent, SyntheticEvent } from "react";
import { PrimaryButtonValidation } from "../atoms/primary-button-validation";
import { createUuid, type UUID } from "src/utils/uuid";
import type { SettingUpdate } from "src/api";
import { clone } from "ramda";

import sizes from "../sizes.scss";
import { store } from "../../store";

/** Converts a PIN digit array to a string, or null if the PIN is incomplete. */
const pinToString = (pin: (number | null)[]): string | null =>
    pin.every((d) => d !== null) ? pin.join("") : null;

/** Parses a PIN string from the backend into a digit array. */
const pinFromString = (value: string | null | undefined): (number | null)[] =>
    value != null ? value.split("").map(Number) : [null, null, null, null];

/** Returns inline styles for a single PIN digit box, reflecting focus and disabled state. */
const pinBoxStyle = (
    enabled: boolean,
    focused: number | null,
    index: number,
    digit: number | null,
): React.CSSProperties => ({
    width: 40,
    height: 40,
    textAlign: "center",
    fontSize: 20,
    fontWeight: 600,
    borderRadius: 2,
    outline: "none",
    border: `1px solid ${
        !enabled
            ? "#c8c6c4"
            : focused === index
              ? "#0078d4"
              : digit !== null
                ? "#605e5c"
                : "#d2d0ce"
    }`,
    backgroundColor: !enabled ? "#f3f2f1" : "#ffffff",
    color: !enabled ? "#a19f9d" : "#323130",
    cursor: !enabled ? "not-allowed" : "text",
    boxShadow: focused === index && enabled ? "0 0 0 1px #0078d4" : "none",
});

const FormUpdateUniversalPin = (): JSX.Element => {
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const validationId = useRef(createUuid()).current;

    const [pin, setPin] = useState<(number | null)[]>([null, null, null, null]);
    const [enabled, setEnabled] = useState<boolean>(true);
    const [focused, setFocused] = useState<number | null>(null);
    const [settingId, setSettingId] = useState<UUID | undefined>();
    const [setting, setSetting] = useState<SettingUpdate | undefined>();
    const [isUpdated, setIsUpdated] = useState(false);

    /**
     * Applies a universalPin value to the current setting, persists it to the
     * validation model, and marks the form as having unsaved changes.
     */
    const applyPinUpdate = (base: SettingUpdate, universalPin: string | null): void => {
        const updatedSetting: SettingUpdate = { ...base, universalPin };
        setSetting(updatedSetting);
        store.repositorySettings.validation.update.updateModel(validationId, updatedSetting);
        setIsUpdated(true);
    };

    useEffect(() => {
        /** Fetches the setting from the backend and initializes local state. */
        const loadSetting = async (): Promise<void> => {
            const [setting] = await store.repositorySettings.byQueryAsync();
            setSettingId(setting.id);
            store.repositorySettings.validation.update.initializeModel(
                validationId,
                clone(setting),
                setting.id,
            );
            setSetting(setting);

            // Pre-populate PIN inputs and checkbox from the persisted setting.
            setPin(pinFromString(setting.universalPin));
            setEnabled(setting.universalPin != null);
        };

        if (settingId == null) {
            loadSetting();
        }

        return () => {
            if (settingId != null) {
                store.repositorySettings.discardMutableCopy(FormUpdateUniversalPin.name, settingId);
            }
        };
    }, [settingId, validationId]);

    /** Handles a single digit being typed into a PIN box and advances focus. */
    const onChangePinSettings = (index: number, value: string): void => {
        if (!/^\d?$/.test(value)) return;

        const newPin = [...pin];
        newPin[index] = value === "" ? null : parseInt(value, 10);
        setPin(newPin);
        applyPinUpdate(clone(setting)!, pinToString(newPin));

        if (value && index < 3) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    /** Handles keyboard navigation (backspace, arrow keys) between PIN boxes. */
    const onKeyDownPinBox = (index: number, e: KeyboardEvent<HTMLInputElement>): void => {
        if (e.key === "Backspace" && pin[index] === null && index > 0)
            inputRefs.current[index - 1]?.focus();
        if (e.key === "ArrowLeft" && index > 0) inputRefs.current[index - 1]?.focus();
        if (e.key === "ArrowRight" && index < 3) inputRefs.current[index + 1]?.focus();
    };

    /** Handles pasting a 4-digit PIN string into the input, filling all boxes at once. */
    const onPastePinBox = (e: ClipboardEvent<HTMLInputElement>): void => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4);
        const newPin: (number | null)[] = [null, null, null, null];
        for (let i = 0; i < pasted.length; i++) newPin[i] = parseInt(pasted[i], 10);
        setPin(newPin);
        applyPinUpdate(clone(setting)!, pinToString(newPin));
        inputRefs.current[Math.min(pasted.length, 3)]?.focus();
    };

    /**
     * Toggles the PIN enabled state.
     * Disabling sets universalPin to null in the model (cleared on save),
     * but keeps the digits visible in the UI until the user confirms.
     * Re-enabling restores universalPin from the current pin state.
     */
    const onChangeEnabled = (_: unknown, checked?: boolean): void => {
        const nowEnabled = !!checked;
        setEnabled(nowEnabled);
        applyPinUpdate(clone(setting)!, nowEnabled ? pinToString(pin) : null);
    };

    /**
     * Submits the updated setting to the backend.
     * If the PIN was disabled, the input fields are cleared after a successful save.
     */
    const onSubmit = async (evt: SyntheticEvent<HTMLFormElement>): Promise<void> => {
        if (settingId === undefined) {
            throw new Error("submit button should be disabled until setting is loaded");
        }
        evt.preventDefault();
        await store.repositorySettings.update(settingId, clone(setting)!);
        setIsUpdated(false);

        if (!enabled) {
            setPin([null, null, null, null]);
        }
    };

    const isComplete = pin.every((d) => d !== null);

    // The save button is disabled when there are no unsaved changes,
    // or when the PIN is enabled but not yet fully filled in.
    const isSaveDisabled = !isUpdated || (!isComplete && enabled);

    return (
        <form onSubmit={onSubmit}>
            <Stack tokens={{ childrenGap: sizes.m }}>
                <Label required disabled={!enabled}>
                    {store.i18n.t("formUpdateUniversalPin.label.label")}
                </Label>
                <Stack horizontal tokens={{ childrenGap: sizes.m }}>
                    {pin.map((digit, index) => (
                        <input
                            key={index}
                            ref={(el) => {
                                inputRefs.current[index] = el;
                            }}
                            type="text"
                            inputMode="numeric"
                            maxLength={1}
                            value={digit !== null ? String(digit) : ""}
                            disabled={!enabled}
                            onChange={(e) => onChangePinSettings(index, e.target.value)}
                            onKeyDown={(e) => onKeyDownPinBox(index, e)}
                            onPaste={onPastePinBox}
                            onFocus={(e) => {
                                setFocused(index);
                                const len = e.target.value.length;
                                e.target.setSelectionRange(len, len);
                            }}
                            onBlur={() => setFocused(null)}
                            style={pinBoxStyle(enabled, focused, index, digit)}
                        />
                    ))}
                </Stack>
                <Separator />
                <Checkbox
                    label={store.i18n.t("formUpdateUniversalPin.enable")}
                    checked={enabled}
                    onChange={onChangeEnabled}
                    styles={{ root: { marginBottom: 16 } }}
                />
                <Stack horizontal horizontalAlign="end">
                    <PrimaryButtonValidation
                        text={store.i18n.t("formUpdateUniversalPin.submit.text")}
                        validation={store.repositorySettings.validation.update}
                        validationId={validationId}
                        primaryDisabled={isSaveDisabled}
                    />
                </Stack>
            </Stack>
        </form>
    );
};

export default FormUpdateUniversalPin;
